import { Box, Typography, useTheme } from '@mui/material';
import { useCallback, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { QuestionProps } from '@/models/Question';
import { QuestionReviewProps } from '@/models/QuestionReview';
import { StandardQuestionDetails } from '@/models/Standard';
import { ApplicationStatus } from '@/utils/constants';

import PortalClosedBanner from '../PortalClosedBanner';
import StandardStatusIcon from './StandardStatusIcon';
import treeCampus from '@/assets/logo-tree-campus.svg';
import treeCityWorldWide from '@/assets/logo-tree-cities-world.svg';
import treeCityUSA from '@/assets/logo-tree-city-usa.svg';
import treeline from '@/assets/logo-tree-line.svg';
import { isTreeCityWorldGrowthAwardApplication } from '@/utils/applicationUtil';
import { StarStatus } from '@/models/Star';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import {
  isLastStandardInList,
  shouldBlockLastStandardNavigation,
} from '@/utils/standardNavigationUtil';

const programIdToImage: { [key: string]: string } = {
  '1': treeCityUSA,
  '2': treeCityWorldWide,
  '3': treeline,
  '4': treeCampus,
  '5': treeCampus,
  '6': treeCampus,
};

function ApplicationHeader() {
  const theme = useTheme();
  const location = useLocation();
  const { selectedProgramId, portalOpen } = useContext(SelectedProgramContext);

  const {
    allComments,
    canEdit,
    currentApplication,
    currentStandardInfo,
    isPreviewMode,
    questions,
    standardError,
    sortedStandards,
    updateCurrentStandardId,
    partnerInfo,
  } = useContext(CurrentApplicationContext);

  const { setAlert } = useContext(SnackbarContext);

  const { translatedText, locale } = useContext(LocaleContext);

  const showApplicationRecognitionYear = useMemo(() => {
    const status = currentApplication.status;
    const year = currentApplication.recognitionYear;

    return (
      status != null &&
      year != null &&
      ![ApplicationStatus.Closed, ApplicationStatus.Denied].includes(status)
    );
  }, [currentApplication.status, currentApplication.recognitionYear]);

  const programImageSrc = useMemo(() => {
    if (questions.programId == null) return null;
    return programIdToImage[String(selectedProgramId)] ?? null;
  }, [questions.programId, selectedProgramId]);

  const handleStandardClick = useCallback(
    (standardId: number) => {
      const isLast = isLastStandardInList(sortedStandards, standardId);
      const shouldBlock = shouldBlockLastStandardNavigation({
        isTargetingLastStandard: isLast,
        applicationTypeId: currentApplication.applicationTypeId,
        programId: currentApplication.programId,
        status: currentApplication.status,
        standardError,
        canEdit,
      });

      if (shouldBlock) {
        if (isPreviewMode) {
          setAlert({
            message:
              'Preview mode: in production this standard is locked until required questions are completed.',
            severity: 'info',
          });
          updateCurrentStandardId(standardId);
          return;
        }

        setAlert({
          message: translatedText.applicationHeader.messageRequiredQuestions,
          severity: 'warning',
        });
        return;
      }

      updateCurrentStandardId(standardId);
    },
    [
      canEdit,
      currentApplication.applicationTypeId,
      currentApplication.programId,
      currentApplication.status,
      isPreviewMode,
      setAlert,
      sortedStandards,
      standardError,
      translatedText.applicationHeader.messageRequiredQuestions,
      updateCurrentStandardId,
    ]
  );

  return (
    <Box display='flex' height='auto' flexDirection='column'>
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        textAlign='center'
        sx={{
          padding: '16px 0 32px 0',
        }}
        gap='8px'
      >
        {programImageSrc != null ? (
          <img style={{ height: '104px' }} src={programImageSrc} alt={'Program Icon'} />
        ) : (
          <Box />
        )}

        <Box
          display={showApplicationRecognitionYear ? 'block' : 'none'}
          sx={{
            backgroundColor: theme.palette.sandShade1.main,
            borderRadius: '32px',
            padding: '4px 8px',
            marginTop: '24px',
          }}
        >
          {translatedText.applicationHeader.yearsRecognized(
            currentApplication.recognitionYear ?? 1
          )}
        </Box>

        <Typography variant='t1'>{partnerInfo?.partnerName}</Typography>
      </Box>

      {location.pathname != '/signup' && questions != null && allComments != null ? (
        <Box display='flex' position='relative' justifyContent='center' marginBottom={1}>
          <Box
            display='flex'
            justifyContent='space-between'
            marginBottom={1}
            height='40px'
            width='100%'
            maxWidth='1050px'
          >
            {questions.standards.map((standard: StandardQuestionDetails | undefined) => {
              if (standard == null) return null;
              // get the comments on the current standard
              const currentComments = allComments?.filter(
                (comment: QuestionReviewProps | null) =>
                  comment != null &&
                  standard.questions
                    ?.map((question: QuestionProps) => question.id)
                    ?.includes(comment.questionId ?? 0)
              );
              const commentsAreResolved: boolean =
                currentComments?.every((comment: QuestionReviewProps) => comment.resolved) ?? false;
              const standardHasUnresolvedComments: boolean =
                currentComments != null && currentComments?.length > 0 && !commentsAreResolved;

              const standardHasError: boolean = !(
                standardError != null && standardError[standard.standardId]
              );

              const standardStar = isTreeCityWorldGrowthAwardApplication(currentApplication)
                ? currentApplication.stars.find((star) => star.standardId === standard.standardId)
                : null;

              const standardHasStar: boolean =
                standardStar != null && standardStar.starStatusId === StarStatus.Awarded;

              return (
                <StandardStatusIcon
                  key={standard.standardId}
                  standard={standard}
                  hasErrors={standardHasError}
                  hasComments={standardHasUnresolvedComments}
                  hasStarAwarded={standardHasStar}
                  currentlySelected={currentStandardInfo?.standardId == standard.standardId}
                  loading={standardError == null}
                  onClick={() =>
                    standard.standardId != null
                      ? handleStandardClick(standard.standardId)
                      : undefined
                  }
                />
              );
            })}
          </Box>

          <Box
            flexBasis='100%'
            width='95%'
            maxWidth='1000px'
            height='40px'
            zIndex='-9999'
            position='absolute'
          >
            <div
              key={`${locale}bar`}
              style={{
                position: 'relative',
                width: '100%',
                height: '20px',
                top: '10px',
                borderTop: `4px dashed ${theme.palette.sandShade4.main}`,
              }}
            ></div>
          </Box>
        </Box>
      ) : (
        <></>
      )}

      {currentApplication.applicationTypeId != null &&
        !portalOpen[currentApplication.applicationTypeId] &&
        !canEdit && <PortalClosedBanner applicationTypeId={currentApplication.applicationTypeId} />}
    </Box>
  );
}

export default ApplicationHeader;
